import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye, Phone, Copy, Truck, Filter,
  RefreshCw, User, Check, X, FileText, Tag, Layout
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi } from '../services/api';
import { formatDate } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import DateRangeFilter from '../components/DateRangeFilter';
import { Modal } from 'antd';
import { usePermissions } from '../hooks/Usepermissions';

// Auth Helper
const getAuthUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch { return null; }
};

// Courier tracking URLs
const COURIER_APIS = {
  'E-kart': { trackingUrl: 'https://ekart-api.flipkart.com/track/' },
  'Fship': { trackingUrl: 'https://shipping-api.com/track/' },
  'Shadowfax': { trackingUrl: 'https://sfxunifiedapi.shadowfax.in/track/' },
  'Delhivery': { trackingUrl: 'https://track.delhivery.com/p/' },
  // Fallback — asli tracking URL backend ke shipment ke `trackUrl` field me aata hai.
  'VELOCITY': { trackingUrl: 'https://shipfastt.in/track/' }
};

const CUSTOMER_STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Convinced to receive', label: 'Convinced to receive' },
  { value: 'Not connected', label: 'Not connected' },
  { value: 'Not interested', label: 'Not interested' },
  { value: 'Others', label: 'Others' },
  { value: 'Update contact number', label: 'Update contact number' }
];

const getStatusColor = (status, isDark) => {
  if (isDark) {
    const colors = {
      'Delivered': 'bg-green-500/10 text-green-400 border-green-500/20',
      'Convinced to receive': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'Not connected': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
      'Not interested': 'bg-red-500/10 text-red-400 border-red-500/20',
      'Others': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Update contact number': 'bg-orange-500/10 text-orange-400 border-orange-500/20'
    };
    return colors[status] || 'bg-white/5 text-gray-500 border-white/10';
  }
  const colors = {
    'Delivered': 'bg-green-100 text-green-700 border-green-300',
    'Convinced to receive': 'bg-blue-100 text-blue-700 border-blue-300',
    'Not connected': 'bg-gray-100 text-gray-700 border-gray-300',
    'Not interested': 'bg-red-100 text-red-600 border-red-300',
    'Others': 'bg-purple-100 text-purple-700 border-purple-300',
    'Update contact number': 'bg-orange-100 text-orange-700 border-orange-300'
  };
  return colors[status] || 'bg-gray-100 text-gray-600 border-gray-200';
};

function NDROrders() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const { has } = usePermissions();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const tableContainerRef = useRef(null);

  const [statusModal, setStatusModal] = useState({
    open: false, orderId: null, status: '', notes: '',
    newContactNumber: '', alternateNumber: '', followUpDate: ''
  });

  const emptyFilters = {
    search: '', orders: 'All Orders', courier: 'All courier',
    courierPartner: 'All Partners', datePreset: '', startDate: '', endDate: '', date: '', agent: 'All Agents',
    category: 'All Categories', status: 'All status', paymentMode: 'All Modes',
    ndrBy: 'Select User', campaign: 'All Campaigns', action: 'Select Action',
    customerStatus: 'All Status'
  };

  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  const courierOptions = ['All courier', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery', { label: 'Velocity', value: 'VELOCITY' }];
  const partnerOptions = ['All Partners', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery', { label: 'Velocity', value: 'VELOCITY' }];
  const statusFilterOptions = ['All status', 'NDR', 'Convinced to receive', 'Not connected', 'Not interested', 'Pending'];
  const ordersPerPageOptions = [10, 20, 50, 100];

  useEffect(() => {
    const user = getAuthUser();
    setCurrentUser(user);
    setUserLoaded(true);
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await ordersApi.getNDREmployees();
      if (Array.isArray(response)) setEmployees(response);
      else if (response?.data) setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching NDR employees:', error);
    }
  }, []);

  useEffect(() => {
    if (currentUser) fetchEmployees();
  }, [currentUser, fetchEmployees]);

  useEffect(() => {
    if (isFilterModalOpen) setTempFilters(activeFilters);
  }, [isFilterModalOpen, activeFilters]);

  const fetchNDROrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page - 1,
        size: ordersPerPage,
      };
      if (activeFilters.courier && activeFilters.courier !== 'All courier') params.courier = activeFilters.courier;
      if (activeFilters.courierPartner && activeFilters.courierPartner !== 'All Partners') params.courierPartner = activeFilters.courierPartner;
      if (activeFilters.orders && activeFilters.orders !== 'All Orders') params.ordersType = activeFilters.orders;
      if (activeFilters.status && activeFilters.status !== 'All status') params.status = activeFilters.status;
      if (activeFilters.search?.trim()) params.searchTerm = activeFilters.search.trim();
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      if (activeFilters.datePreset) params.dateRangePreset = activeFilters.datePreset;
      if (activeFilters.date && !activeFilters.startDate) params.date = activeFilters.date;
      if (activeFilters.category && activeFilters.category !== 'All Categories') params.category = activeFilters.category;
      if (activeFilters.paymentMode && activeFilters.paymentMode !== 'All Modes') params.paymentMode = activeFilters.paymentMode;
      if (activeFilters.ndrBy && activeFilters.ndrBy !== 'Select User') params.ndrBy = activeFilters.ndrBy;
      if (activeFilters.campaign && activeFilters.campaign !== 'All Campaigns') params.campaign = activeFilters.campaign;
      if (activeFilters.customerStatus && activeFilters.customerStatus !== 'All Status') params.customerStatus = activeFilters.customerStatus;
      
      if (currentUser && currentUser.role !== 'ADMIN') {
        params.employeeId = currentUser.id;
      } else if (activeFilters.agent && activeFilters.agent !== 'All Agents') {
        const employee = employees.find(e => e.name === activeFilters.agent);
        if (employee) params.employeeId = employee.id;
        else params.agent = activeFilters.agent;
      }

      const response = await ordersApi.getNDROrders(params);
      let ordersData = [], total = 0;
      if (response?.orders) {
        ordersData = response.orders;
        total = response.total || response.totalPages * response.pageSize || response.orders.length;
      } else if (response?.data) {
        ordersData = response.data;
        total = response.total || response.data.length;
      } else if (response?.content) {
        ordersData = response.content;
        total = response.totalElements || response.content.length;
      } else if (Array.isArray(response)) {
        ordersData = response;
        total = response.length;
      }

      const mappedOrders = ordersData.map((order, index) => ({
        id: order.id || index + 1,
        orderId: order.orderId || `OD${order.id}`,
        orderDate: (order.orderDate || order.createdAt) ? formatDate(order.orderDate || order.createdAt) : '-',
        orderBy: order.assignedTo || order.employee?.name || '-',
        orderByEmp: order.orderByEmp || order.employeeId || order.employee?.employeeId || '-',
        customerName: order.customerName || order.lead?.customerName || '-',
        phone: order.contactNumber || order.phone || order.lead?.contactNumber || '-',
        whatsappVerified: order.whatsappVerified || order.isWhatsappVerified || false,
        product: order.productName || order.product?.name || order.orderItems?.[0]?.productName || '-',
        category: order.category || order.product?.category || order.orderItems?.[0]?.category || '-',
        amount: order.grandTotal || order.amount || order.restAmount || 0,
        orderCampaign: order.orderCampaign || order.campaign || order.leadSource || '-',
        status: order.status || order.ndrStatus || 'NDR',
        ndrBy: order.dispatchedByName || order.ndrBy || '-/-',
        customerStatus: order.customerStatus || '',
        cPartner: order.cPartner || order.courierPartner || order.courierName || '-',
        courier: order.courierName || order.courier || '-',
        awbNumber: order.trackingNumber || order.awbNumber || '-',
        remarks: order.remarks || '',
        attemptCount: order.attemptCount || 0,
      }));

      setOrders(mappedOrders);
      setTotalOrders(total);
      setSelectedOrders([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching NDR orders:', error);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, ordersPerPage, currentUser, employees]);

  // ✅ Single API call on filter / page / size change
  useEffect(() => {
    if (userLoaded) {
      fetchNDROrders(currentPage);
    }
  }, [currentPage, userLoaded, fetchNDROrders]);

  const handleSelectAll = () => {
    if (selectAll) setSelectedOrders([]);
    else setSelectedOrders(orders.map(o => o.id));
    setSelectAll(!selectAll);
  };

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    else setSelectedOrders([...selectedOrders, orderId]);
  };

  const handleCustomerStatusChange = (orderId, newStatus) => {
    if (!newStatus) return;
    setStatusModal({ open: true, orderId, status: newStatus, notes: '', newContactNumber: '', alternateNumber: '', followUpDate: '' });
  };

  const handleStatusModalSubmit = async () => {
    const { orderId, status, notes, newContactNumber, alternateNumber, followUpDate } = statusModal;
    try {
      const payload = { status: status.toLowerCase(), notes: notes || '' };
      if (status === 'Convinced to receive' && followUpDate) payload.followUpDate = followUpDate;
      if (status === 'Update contact number') { payload.newContactNumber = newContactNumber; payload.alternateNumber = alternateNumber; }
      await ordersApi.updateNDRCustomerStatus(orderId, payload);
      setOrders(orders.map(o => o.id === orderId ? { ...o, customerStatus: status } : o));
      setStatusModal({ open: false, orderId: null, status: '', notes: '', newContactNumber: '', alternateNumber: '', followUpDate: '' });
      const refreshStatuses = ['Delivered', 'Not interested', 'Convinced to receive'];
      if (refreshStatuses.includes(status)) setTimeout(() => fetchNDROrders(), 1000);
    } catch (error) {
      console.error('Error updating status:', error);
      Modal.error({ title: 'Error', content: 'Failed to update status: ' + (error.message || 'Unknown error') });
    }
  };

  const handleAssignOrders = async () => {
    if (!selectedEmployee) { Modal.warning({ title: 'Selection Required', content: 'Please select an employee' }); return; }
    if (selectedOrders.length === 0) { Modal.warning({ title: 'Selection Required', content: 'Please select at least one order' }); return; }
    try {
      const payload = { orderIds: selectedOrders, assignedToEmployeeId: selectedEmployee.id, assignmentNotes: `Assigned by ${currentUser?.name || 'Admin'}` };
      const response = await ordersApi.assignNDROrders(payload);
      if (response?.successCount !== undefined) {
        Modal.success({ title: 'Success', content: `Successfully assigned ${response.successCount} orders to ${selectedEmployee.name}. Failed: ${response.failCount || 0}` });
      } else {
        Modal.success({ title: 'Success', content: `Successfully assigned ${selectedOrders.length} orders to ${selectedEmployee.name}` });
      }
      setSelectedOrders([]);
      setSelectAll(false);
      setSelectedEmployee(null);
      fetchNDROrders();
    } catch (error) {
      console.error('Error assigning orders:', error);
      Modal.error({ title: 'Assignment Failed', content: 'Failed to assign orders: ' + (error.message || 'Unknown error') });
    }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);
  const handleOrderDetails = (orderId) => navigate(`/orders/amount-decrease/${orderId}`, { state: { activeTab: 'NDR' } });
  const handleRefresh = () => fetchNDROrders(currentPage);

  const canAssign = has('NDR_RESOLVE');
  const canExport = has('NDR_EXPORT');
  const canFilter = has('NDR_FILTER');
  const canEdit = has('NDR_EDIT');

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="flex flex-col gap-6">

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFilterModalOpen(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] p-4 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-full">
            <div className="bg-white rounded-xl w-full max-w-4xl mx-auto flex flex-col shadow-xl my-8 relative">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
                <h2 className="text-lg font-bold text-white">Filter Orders</h2>
                <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                    <ExpandableSearchBar searchTerm={tempFilters.search} onSearchChange={(value) => setTempFilters({ ...tempFilters, search: value })} placeholder="Search..." fullWidth className="w-full" />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Orders</label>
                    <ThemeSelect value={tempFilters.orders} onChange={(val) => setTempFilters({ ...tempFilters, orders: val })} options={['All Orders', 'Fresh Orders', 'Re-assigned Orders']} className="w-full" />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Courier</label>
                    <ThemeSelect value={tempFilters.courier} onChange={(val) => setTempFilters({ ...tempFilters, courier: val })} options={courierOptions} className="w-full" icon={Truck} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Courier Partner</label>
                    <ThemeSelect value={tempFilters.courierPartner} onChange={(val) => setTempFilters({ ...tempFilters, courierPartner: val })} options={partnerOptions} className="w-full" />
                  </div>
                  <div className="col-span-1">
                    <DateRangeFilter
                      label="Date"
                      placeholder="All Time"
                      preset={tempFilters.datePreset}
                      startDate={tempFilters.startDate}
                      endDate={tempFilters.endDate}
                      onChange={({ preset, startDate, endDate }) =>
                        setTempFilters({
                          ...tempFilters,
                          datePreset: preset,
                          startDate: startDate || '',
                          endDate: endDate || '',
                          date: startDate || '',
                        })
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Agents</label>
                    <ThemeSelect value={tempFilters.agent} onChange={(val) => setTempFilters({ ...tempFilters, agent: val })} options={['All Agents', ...employees.map(e => e.name)]} className="w-full" icon={User} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <ThemeSelect value={tempFilters.category} onChange={(val) => setTempFilters({ ...tempFilters, category: val })} options={['All Categories', 'Ayurvedic Men Wellness', 'Ayurvedic', 'Wellness']} className="w-full" icon={Tag} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <ThemeSelect value={tempFilters.status} onChange={(val) => setTempFilters({ ...tempFilters, status: val })} options={statusFilterOptions} className="w-full" icon={RefreshCw} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                    <ThemeSelect value={tempFilters.paymentMode} onChange={(val) => setTempFilters({ ...tempFilters, paymentMode: val })}
                      options={['All Modes', 'COD', 'ONLINE', 'UPI', 'CARD']} className="w-full" />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">NDR By</label>
                    <ThemeSelect value={tempFilters.ndrBy} onChange={(val) => setTempFilters({ ...tempFilters, ndrBy: val })} options={['Select User', ...employees.map(e => e.name)]} className="w-full" icon={User} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
                    <ThemeSelect value={tempFilters.campaign} onChange={(val) => setTempFilters({ ...tempFilters, campaign: val })} options={['All Campaigns', 'Campaign 1', 'Campaign 2']} className="w-full" icon={Layout} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                    <ThemeSelect value={tempFilters.action} onChange={(val) => setTempFilters({ ...tempFilters, action: val })} options={['Select Action', 'Export', 'Print', 'Delete']} className="w-full" />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Status</label>
                    <ThemeSelect value={tempFilters.customerStatus} onChange={(val) => setTempFilters({ ...tempFilters, customerStatus: val })} options={['All Status', ...CUSTOMER_STATUS_OPTIONS.map(opt => opt.label).filter(l => l !== 'Select Status')]} className="w-full" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setTempFilters(emptyFilters)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >Reset</button>
                <button onClick={() => { setActiveFilters(tempFilters); setCurrentPage(1); setIsFilterModalOpen(false); }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: currentTheme.color }}>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>NDR</h1>
            <span className="text-gray-500 text-lg desktop:text-2xl">({totalOrders.toLocaleString()})</span>
          </div>
          <div className="flex items-center space-x-3">
            {currentUser && (
              <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 desktop:px-4 desktop:py-3 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
                <div className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-teal-100'}`}>
                  <User className={`w-4 h-4 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-teal-600'}`} />
                </div>
                <div className="text-sm desktop:text-base">
                  <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{currentUser.name}</p>
                  <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{currentUser.role}</p>
                </div>
              </div>
            )}
            <button onClick={handleRefresh} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
              <RefreshCw className={`w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {/* Filter Button — NDR_FILTER */}
            {canFilter && (
              <button onClick={() => setIsFilterModalOpen(true)} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500" />
              </button>
            )}
            {/* Export Button — NDR_EXPORT */}
            {canExport && (
              <ExcelExportButton
                onClick={() => {}} // Placeholder or real logic if available
                isDownloading={loading}
              />
            )}
          </div>
        </div>

        {/* Quick Filters Bar */}
        <div className="flex items-center space-x-4 mb-6 flex-wrap gap-y-3">
          <ExpandableSearchBar
            searchTerm={activeFilters.search}
            onSearchChange={(value) => { setActiveFilters(prev => ({ ...prev, search: value })); setCurrentPage(1); }}
            placeholder="Search by lead id or customer name"
          />
          <ThemeSelect value={`${ordersPerPage} Orders`} onChange={(val) => {
            const newSize = parseInt(val);
            setOrdersPerPage(newSize);
            setCurrentPage(1);
          }} options={ordersPerPageOptions.map(opt => `${opt} Orders`)} className="w-44 desktop:w-60" />
          <ThemeSelect value={activeFilters.courier} onChange={(val) => { setActiveFilters(prev => ({ ...prev, courier: val })); setCurrentPage(1); }} options={courierOptions} icon={Truck} className="w-48 desktop:w-64" />
          <ThemeSelect value={activeFilters.status} onChange={(val) => { setActiveFilters(prev => ({ ...prev, status: val })); setCurrentPage(1); }} options={statusFilterOptions} icon={RefreshCw} className="w-52 desktop:w-72" />
          <ThemeSelect value={activeFilters.courierPartner} onChange={(val) => { setActiveFilters(prev => ({ ...prev, courierPartner: val })); setCurrentPage(1); }} options={partnerOptions} icon={User} className="w-52 desktop:w-72" />
        </div>

        {/* Status Update Modal */}
        {statusModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ background: currentTheme.color }}>
                <h2 className="text-lg font-bold text-white">Update Status — {statusModal.status}</h2>
                <button onClick={() => setStatusModal({ ...statusModal, open: false })} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5 text-white" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes {statusModal.status === 'Update contact number' ? '(Optional)' : ''}</label>
                  <textarea value={statusModal.notes} onChange={(e) => setStatusModal({ ...statusModal, notes: e.target.value })} placeholder="Enter notes..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 resize-none" style={{ ringColor: currentTheme.color, '--tw-ring-color': currentTheme.color }} />
                </div>
                {statusModal.status === 'Convinced to receive' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Date</label>
                    <input type="date" value={statusModal.followUpDate} onChange={(e) => setStatusModal({ ...statusModal, followUpDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ ringColor: currentTheme.color, '--tw-ring-color': currentTheme.color }} />
                  </div>
                )}
                {statusModal.status === 'Update contact number' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Contact Number <span className="text-red-500">*</span></label>
                      <input type="tel" value={statusModal.newContactNumber} onChange={(e) => setStatusModal({ ...statusModal, newContactNumber: e.target.value })} placeholder="Enter new number" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ ringColor: currentTheme.color, '--tw-ring-color': currentTheme.color }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Number</label>
                      <input type="tel" value={statusModal.alternateNumber} onChange={(e) => setStatusModal({ ...statusModal, alternateNumber: e.target.value })} placeholder="Enter alternate number" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ ringColor: currentTheme.color, '--tw-ring-color': currentTheme.color }} />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end px-6 py-4 border-t bg-gray-50 space-x-3">
                <button onClick={() => setStatusModal({ ...statusModal, open: false })} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleStatusModalSubmit} disabled={statusModal.status === 'Update contact number' && !statusModal.newContactNumber}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: currentTheme.color }}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${canAssign ? 'flex-1' : 'w-full'} ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
            <div ref={tableContainerRef} className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead className="text-white" style={{ background: currentTheme.color }}>
                  <tr>
                    {canAssign && (
                      <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center w-12">
                        <button onClick={handleSelectAll}
                          className={`w-6 h-6 desktop:w-8 desktop:h-8 rounded border-2 flex items-center justify-center transition-colors`}
                          style={{
                            backgroundColor: selectAll ? 'white' : 'transparent',
                            borderColor: selectAll ? 'white' : 'rgba(255, 255, 255, 0.5)'
                          }}>
                          {selectAll && <Check className="w-4 h-4 desktop:w-6 desktop:h-6" style={{ color: currentTheme.color }} />}
                        </button>
                      </th>
                    )}
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Order ID</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Date</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Order By</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Name</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Product</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Category</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Rest Amt.</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Order Campaign</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">NDR By</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Customer Status</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">C.Partner</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">Courier</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase">AWB No.</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-xs desktop:text-base font-semibold uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {loading ? (
                    <tr>
                      <td colSpan={canAssign ? 16 : 15} className="px-4 py-12 text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}></div>
                          <span className="text-gray-500">Loading NDR orders...</span>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={canAssign ? 16 : 15} className="px-4 py-12 text-center text-gray-500">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p>No NDR orders found</p>
                      </td>
                    </tr>
                  ) : orders.map((order, idx) => (
                    <tr key={order.id} className={`transition-colors ${isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50'}`}>
                      {canAssign && (
                        <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                          <button onClick={() => handleSelectOrder(order.id)}
                            className={`w-6 h-6 desktop:w-8 desktop:h-8 rounded-full border-2 flex items-center justify-center transition-colors`}
                            style={{
                              backgroundColor: selectedOrders.includes(order.id) ? currentTheme.color : 'transparent',
                              borderColor: selectedOrders.includes(order.id) ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.25)' : '#d1d5db')
                            }}
                          >
                            {selectedOrders.includes(order.id) && <Check className="w-3 h-3 desktop:w-5 desktop:h-5 text-white" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.orderId}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">{order.orderDate}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.orderBy}</span>
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.orderByEmp}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customerName}</span>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 desktop:w-4 desktop:h-4 text-gray-500" />
                            <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.phone}</span>
                            {order.whatsappVerified && (
                              <div className="w-4 h-4 desktop:w-5 desktop:h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 desktop:w-3 desktop:h-3 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block max-w-[150px]`}>{order.product}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{order.category}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className="text-sm desktop:text-base font-semibold" style={{ color: currentTheme.color }}>₹ {order.amount.toLocaleString()}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.orderCampaign}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.status}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.ndrBy}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        {canEdit ? (
                          <select value={order.customerStatus} onChange={(e) => handleCustomerStatusChange(order.id, e.target.value)}
                            className={`text-sm desktop:text-base px-3 py-1.5 desktop:px-4 desktop:py-2 rounded-lg border cursor-pointer focus:outline-none focus:ring-2`}
                            style={{ ringColor: currentTheme.color, '--tw-ring-color': currentTheme.color, ...(order.customerStatus ? getStatusColor(order.customerStatus, isDark) : {backgroundColor: isDark ? '#1f2937' : 'white', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#d1d5db', color: isDark ? 'white' : 'black'}) }}>
                            {CUSTOMER_STATUS_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value} className={isDark ? 'bg-[#111827] text-white' : ''}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`text-sm px-3 py-1.5 rounded-lg border ${order.customerStatus ? getStatusColor(order.customerStatus, isDark) : (isDark ? 'bg-white/5 text-gray-400 border-white/10' : 'bg-gray-100 text-gray-500 border-gray-200')}`}>
                            {order.customerStatus || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.cPartner}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.courier}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg transition-all ${isDark ? 'bg-white/5 border border-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}>
                          <a href={`${COURIER_APIS[order.courier]?.trackingUrl || '#'}${order.awbNumber}`} target="_blank" rel="noopener noreferrer" className={`text-sm desktop:text-base font-mono font-bold hover:underline`} style={{ color: isDark ? '#fbbf24' : currentTheme.color }}>{order.awbNumber}</a>
                          <button onClick={() => copyToClipboard(order.awbNumber)} className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`}><Copy className={`w-3.5 h-3.5 desktop:w-4 desktop:h-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                        <button onClick={() => handleOrderDetails(order.id)}
                          className={`p-2 desktop:p-3 rounded-xl transition-all ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                          style={{ color: currentTheme.color }}>
                          <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalOrders}
              itemsPerPage={ordersPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
              }}
            />
          </div>

          {/* Assign Panel */}
          {canAssign && (
            <div className={`w-72 desktop:w-96 rounded-xl shadow-sm border h-fit ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
              <div className={`p-4 desktop:p-6 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                <h3 className={`text-lg desktop:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Assign to</h3>
              </div>
              <div className="p-4 desktop:p-6 max-h-[500px] desktop:max-h-[600px] overflow-y-auto">
                <div className="space-y-2 desktop:space-y-4">
                  {employees.map(employee => (
                    <button key={employee.id} onClick={() => setSelectedEmployee(employee)}
                      className={`w-full flex items-center space-x-3 p-3 desktop:p-4 rounded-xl transition-all border-2`}
                      style={{
                        backgroundColor: selectedEmployee?.id === employee.id ? `${currentTheme.color}1A` : 'transparent',
                        borderColor: selectedEmployee?.id === employee.id ? currentTheme.color : 'transparent'
                      }}>
                      <div
                        className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all`}
                        style={{ backgroundColor: selectedEmployee?.id === employee.id ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB') }}>
                        {selectedEmployee?.id === employee.id
                          ? <Check className="w-4 h-4 desktop:w-5 desktop:h-5 text-white" />
                          : <User className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />}
                      </div>
                      <div className="text-left">
                        <p className={`text-sm desktop:text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{employee.name}</p>
                        <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {employee.employeeId || employee.id}</p>
                      </div>
                    </button>
                  ))}
                  {employees.length === 0 && <p className={`text-sm desktop:text-base text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No employees found</p>}
                </div>
              </div>
              <div className={`p-4 desktop:p-6 border-t ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'} rounded-b-xl`}>
                <button onClick={handleAssignOrders} disabled={!selectedEmployee || selectedOrders.length === 0}
                  className={`w-full py-3 desktop:py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
                  style={{
                    backgroundColor: currentTheme.color,
                    boxShadow: selectedEmployee && selectedOrders.length > 0 ? `0 8px 20px -5px ${currentTheme.color}60` : 'none',
                    color: 'white'
                  }}>
                  Assign {selectedOrders.length > 0 ? `(${selectedOrders.length})` : ''}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default NDROrders;
